import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from './authService';
import CleaningPeriodFilter from './CleaningPeriodFilter';
import { CleaningPeriod, defaultPeriod, filterJobsByPeriod, periodLabel, isFutureISO } from './cleaningWeek';
import { buildPaymentSummary, exportPaymentsPDF, exportPaymentsWord } from './cleaningPayments';

// 🎨 Το ΓΑΛΑΖΙΟ του GatePage (page background gradient — GatePage.tsx:627)
const GATE_BLUE = 'linear-gradient(180deg, #6bb6f5 0%, #1e90ff 50%, #87ceeb 100%)';

// 📅 ISO (yyyy-mm-dd) → ευρωπαϊκή εμφάνιση dd/mm/yyyy. Βάση μένει ISO.
export const formatDateEU = (iso?: string): string => {
  if (!iso) return '';
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(iso);
};

// 🧽 CleaningDashboard — οθόνη για το συνεργείο καθαρισμού (role CLEANING).
// Δύο tabs: (1) Booking Plan read-only (κωδικός/ημ/νία/σκάφος, ΧΩΡΙΣ χρήματα),
//           (2) Καθαρισμοί — κάρτες σκαφών προς καθαρισμό με πεδία που συμπληρώνει το συνεργείο.
const CleaningDashboard: React.FC = () => {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const code = user?.code || '';

  // Guard: μόνο CLEANING (το ουσιαστικό gate είναι server-side)
  useEffect(() => {
    const r = (user?.role || '').toUpperCase();
    if (!user) { navigate('/'); return; }
    if (r !== 'CLEANING') { navigate('/admin'); }
  }, []); // eslint-disable-line

  const [tab, setTab] = useState<'plan' | 'jobs' | 'pay' | 'company'>('jobs');
  const [bookings, setBookings] = useState<any[]>([]);
  const [jobs, setJobs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState<any>({});       // 🏢 στοιχεία εταιρείας (own)
  const [companySaved, setCompanySaved] = useState(false);
  const [now] = useState(() => new Date());                       // 📅 σταθερό "τώρα" για week filter
  const [period, setPeriod] = useState<CleaningPeriod>(defaultPeriod); // default: τρέχουσα εβδομάδα
  const [expanded, setExpanded] = useState<string | null>(null);  // 💰 drill-down ανά σκάφος

  const api = (opts: any = {}) => fetch('/api/cleaning-jobs.php' + (opts.qs || ''), {
    method: opts.method || 'GET',
    headers: { 'Content-Type': 'application/json', 'X-Employee-Code': code },
    ...(opts.body ? { body: JSON.stringify(opts.body) } : {})
  });

  const loadJobs = async () => {
    setLoading(true);
    try { const j = await (await api()).json(); setJobs(j.jobs || []); } catch (e) {}
    setLoading(false);
  };
  const loadBookings = async () => {
    try { const j = await (await api({ qs: '?action=bookings' })).json(); setBookings(j.bookings || []); } catch (e) {}
  };
  const loadCompany = async () => {
    try {
      const j = await (await fetch('/api/cleaning-company.php', { headers: { 'X-Employee-Code': code } })).json();
      setCompany((j.companies && j.companies[0]) || {});
    } catch (e) {}
  };
  const saveCompany = async () => {
    try {
      await fetch('/api/cleaning-company.php', {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Employee-Code': code },
        body: JSON.stringify({
          company_name: company.company_name || '', address: company.address || '', afm: company.afm || '',
          doy: company.doy || '', email: company.email || '', phone: company.phone || '', iban: company.iban || ''
        })
      });
      setCompanySaved(true); setTimeout(() => setCompanySaved(false), 2500);
    } catch (e) {}
  };
  useEffect(() => { loadJobs(); loadBookings(); loadCompany(); }, []); // eslint-disable-line

  const setLocal = (id: number, patch: any) =>
    setJobs(prev => prev.map(j => j.id === id ? { ...j, ...patch } : j));

  const saveField = async (id: number, patch: any) => {
    setLocal(id, patch);
    try { await api({ method: 'PUT', body: { id, ...patch } }); } catch (e) {}
  };

  // 🇬🇷 toggle "καθαρίστηκε": save + reload ώστε να φανεί το auto end_time (Ελλάδας) από τον server
  const toggleCleaned = async (id: number, checked: boolean) => {
    // 🛡️ guard: μη μαρκάρεις "καθαρίστηκε" καθαρισμό για μελλοντική ημερομηνία (αύριο/μέλλον)
    if (checked) {
      const job = jobs.find(j => j.id === id);
      if (job && isFutureISO(job.cleaned_date, now) &&
          !window.confirm(`Ο καθαρισμός είναι για μελλοντική ημερομηνία (${formatDateEU(job.cleaned_date)}). Σίγουρα καθαρίστηκε;`)) {
        setJobs(prev => [...prev]); // force re-render ώστε το controlled checkbox να ξαναγίνει unchecked
        return; // ακύρωση — τίποτα δεν αλλάζει
      }
    }
    setLocal(id, { cleaned: checked });
    try { await api({ method: 'PUT', body: { id, cleaned: checked } }); await loadJobs(); } catch (e) {}
  };

  // 📅 φίλτρο περιόδου (default τρέχουσα εβδομάδα) — ισχύει σε Καθαρισμούς + Πληρωμές
  const filteredJobs = filterJobsByPeriod(jobs, period, now);
  const total = filteredJobs.reduce((s, j) => s + (parseFloat(j.amount) || 0), 0);
  const paySummary = buildPaymentSummary(filteredJobs);           // 💰 συγκεντρωτικά ανά σκάφος
  const periodTxt = periodLabel(period, now);
  // BOOKING PLAN: αύξουσα κατά ημερομηνία έναρξης (παλιό→νέο). ISO sort = χρονολογικό.
  const sortedBookings = [...bookings].sort((a, b) =>
    String(a.cleaned_date || '').localeCompare(String(b.cleaned_date || '')));
  // 🧽 cleaned booking_numbers (από τα jobs) → πράσινη γραμμή στο Booking Plan
  const cleanedSet = new Set(jobs.filter(j => j.cleaned).map(j => j.booking_number));
  // 📅 ΗΜΕΡΟΜΗΝΙΑ ΝΑΥΛΟΥ ανά booking_number (action=bookings → startDate ως cleaned_date· ΟΧΙ χρήματα)
  const charterDateByNumber: Record<string, string> = {};
  bookings.forEach((b: any) => { if (b.booking_number) charterDateByNumber[b.booking_number] = b.cleaned_date; });

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #e0f7ff 0%, #b3e5fc 50%, #81d4fa 100%)' }}>
      {/* Top bar — γαλάζιο GatePage */}
      <div className="flex items-center justify-between p-3 text-white shadow" style={{ background: GATE_BLUE }}>
        <div className="flex items-center gap-2 font-bold text-lg"><span>🧽</span><span>Καθαρισμοί</span></div>
        <div className="text-sm">{user?.name || code}</div>
        <button onClick={() => { authService.logout(); sessionStorage.clear(); window.location.replace('/'); }}
          className="text-xs bg-black/20 hover:bg-black/30 px-3 py-1 rounded">Έξοδος</button>
      </div>

      {/* tabs */}
      <div className="flex gap-2 p-3">
        <button onClick={() => setTab('plan')}
          style={tab === 'plan' ? { background: GATE_BLUE } : undefined}
          className={`flex-1 py-3 rounded-xl font-bold shadow text-sm ${tab === 'plan' ? 'text-white' : 'bg-white text-blue-700'}`}>
          📅 Booking Plan
        </button>
        <button onClick={() => setTab('jobs')}
          style={tab === 'jobs' ? { background: GATE_BLUE } : undefined}
          className={`flex-1 py-3 rounded-xl font-bold shadow text-sm ${tab === 'jobs' ? 'text-white' : 'bg-white text-blue-700'}`}>
          🧽 Καθαρισμοί
        </button>
        <button onClick={() => setTab('pay')}
          style={tab === 'pay' ? { background: GATE_BLUE } : undefined}
          className={`flex-1 py-3 rounded-xl font-bold shadow text-sm ${tab === 'pay' ? 'text-white' : 'bg-white text-blue-700'}`}>
          💰 Πληρωμές
        </button>
        <button onClick={() => setTab('company')}
          style={tab === 'company' ? { background: GATE_BLUE } : undefined}
          className={`flex-1 py-3 rounded-xl font-bold shadow text-sm ${tab === 'company' ? 'text-white' : 'bg-white text-blue-700'}`}>
          🏢 Στοιχεία Εταιρείας
        </button>
      </div>

      <div className="p-3 space-y-3">
        {/* TAB 1: Booking Plan read-only (no money) */}
        {tab === 'plan' && (
          sortedBookings.length === 0
            ? <div className="text-center text-blue-700 py-8">Καμία κράτηση.</div>
            : sortedBookings.map((b, i) => {
              const isClean = cleanedSet.has(b.booking_number); // 🧽 cleaned → πράσινη γραμμή
              return (
              <div key={i} className={`rounded-lg p-3 shadow flex justify-between items-center ${isClean ? 'bg-emerald-500' : 'bg-white'}`}>
                <div>
                  <div className={`font-bold ${isClean ? 'text-white' : 'text-blue-900'}`}>{b.vessel_name || '—'} <span className="font-semibold" title="Άτομα (σύνολο crew list)">{`👥 ${(b as any).people != null && (b as any).people > 0 ? (b as any).people : '—'}`}</span></div>
                  <div className={`text-xs font-mono ${isClean ? 'text-emerald-50' : 'text-gray-500'}`}>{b.booking_number}</div>
                </div>
                <div className={`text-sm ${isClean ? 'text-emerald-50' : 'text-gray-600'}`}>{formatDateEU(b.cleaned_date)}</div>
              </div>
              );
            })
        )}

        {/* TAB 2: Cleaning cards */}
        {tab === 'jobs' && (
          <>
            <CleaningPeriodFilter period={period} now={now} onChange={setPeriod} />
            {loading ? <div className="text-center text-teal-700 py-8">Φόρτωση…</div>
             : filteredJobs.length === 0 ? <div className="text-center text-teal-700 py-8">Δεν υπάρχουν σκάφη προς καθαρισμό σε αυτή την περίοδο.</div>
             : <>
            {filteredJobs.map(job => (
              <div key={job.id} className="bg-white rounded-lg p-3 shadow">
                <div className="flex justify-between items-start gap-2">
                  <div className="min-w-0">
                    <div className="font-bold text-teal-800 text-lg leading-tight">
                      {job.vessel_name || '—'}
                      {job.booking_number && <span className="text-sm font-semibold text-gray-500"> — {job.booking_number}</span>}
                    </div>
                    {charterDateByNumber[job.booking_number] && (
                      <div className="text-xs text-gray-500">Ναύλος: {formatDateEU(charterDateByNumber[job.booking_number])}</div>
                    )}
                  </div>
                  <span className={`shrink-0 px-2 py-1 rounded text-sm font-bold ${job.paid ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {job.paid ? '✅ Πληρώθηκε' : '⏳ Εκκρεμεί'}
                  </span>
                </div>

                {/* Πεδία (αριστερά) + Οδηγίες στο κενό (δεξιά) */}
                <div className="flex gap-3 mt-2">
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 flex-1">
                    <div className="text-xs text-gray-500">Ώρα έναρξης
                      <div className="mt-0.5 px-2 py-1 bg-gray-100 border border-gray-200 rounded text-base font-semibold text-gray-700">{job.start_time || '—'}</div>
                    </div>
                    <label className="text-xs text-gray-500">Παραδόθηκε
                      <input key={`end-${job.id}-${job.end_time || ''}`} type="time" defaultValue={job.end_time || ''}
                        onBlur={e => saveField(job.id, { end_time: e.target.value })}
                        className="w-full mt-0.5 px-2 py-1 border border-gray-300 rounded text-base" />
                    </label>
                    <div className="text-xs text-gray-500">Να τελειώσει μέχρι
                      <div className="mt-0.5 px-2 py-1 bg-gray-100 border border-gray-200 rounded text-base font-semibold text-gray-700">{job.deadline_time || '—'}</div>
                    </div>
                    <label className="text-xs text-gray-500">Ημερομηνία
                      <input type="date" defaultValue={job.cleaned_date || ''}
                        onBlur={e => saveField(job.id, { cleaned_date: e.target.value })}
                        className="w-full mt-0.5 px-2 py-1 border border-gray-300 rounded text-base" />
                    </label>
                    <label className="text-xs text-gray-500">Ποσό €
                      <input type="number" step="0.01" defaultValue={job.amount ?? ''}
                        onBlur={e => saveField(job.id, { amount: e.target.value })}
                        className="w-full mt-0.5 px-2 py-1 border border-gray-300 rounded text-base" />
                    </label>
                  </div>

                  {/* Οδηγίες — γεμίζουν το κενό δίπλα στις ώρες */}
                  {job.admin_instructions && (
                    <div className="flex-1 p-2 bg-amber-50 rounded text-sm text-amber-800">
                      <b>Οδηγίες:</b> {job.admin_instructions}
                    </div>
                  )}
                </div>

                {/* Καθαρίστηκε + READY */}
                <label className="flex items-center gap-2 mt-2 text-base font-semibold text-teal-700">
                  <input type="checkbox" checked={!!job.cleaned}
                    onChange={e => toggleCleaned(job.id, e.target.checked)}
                    className="w-5 h-5" />
                  Καθαρίστηκε
                  {job.cleaned && (
                    <span className="ml-1 px-2 py-0.5 text-xs font-black text-black rounded border border-blue-400 animate-pulse"
                      style={{ background: '#87ceeb' }}>READY</span>
                  )}
                </label>
              </div>
            ))}
            <div className="bg-white rounded-lg p-4 shadow flex justify-between items-center font-bold">
              <span className="text-gray-700">Σύνολο</span>
              <span className="text-teal-600 text-lg">{total.toFixed(2)} €</span>
            </div>
             </>}
          </>
        )}

        {/* TAB 3: 💰 Πληρωμές — συγκεντρωτικά ανά σκάφος (CLEANING: read-only view + export) */}
        {tab === 'pay' && (
          <>
            <CleaningPeriodFilter period={period} now={now} onChange={setPeriod} />
            {paySummary.rows.length === 0 ? (
              <div className="text-center text-teal-700 py-8">Δεν υπάρχουν καθαρισμοί σε αυτή την περίοδο.</div>
            ) : (
              <>
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-white" style={{ background: GATE_BLUE }}>
                        <th className="text-left px-3 py-2">Σκάφος</th>
                        <th className="text-center px-2 py-2">Καθ.</th>
                        <th className="text-right px-2 py-2">Χρέωση</th>
                        <th className="text-right px-2 py-2">Πληρώθηκε</th>
                        <th className="text-right px-3 py-2">Υπόλοιπο</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paySummary.rows.map(r => (
                        <React.Fragment key={r.vessel_name}>
                          <tr className="border-t border-gray-100 cursor-pointer hover:bg-blue-50"
                            onClick={() => setExpanded(expanded === r.vessel_name ? null : r.vessel_name)}>
                            <td className="px-3 py-2 font-semibold text-blue-900">
                              <span className="text-gray-400 mr-1">{expanded === r.vessel_name ? '▾' : '▸'}</span>{r.vessel_name}
                            </td>
                            <td className="text-center px-2 py-2 text-gray-600">{r.count}</td>
                            <td className="text-right px-2 py-2">{r.charged.toFixed(2)} €</td>
                            <td className="text-right px-2 py-2 text-green-700">{r.paid.toFixed(2)} €</td>
                            <td className={`text-right px-3 py-2 font-bold ${r.balance === 0 ? 'text-green-600' : 'text-red-600'}`}>{r.balance.toFixed(2)} €</td>
                          </tr>
                          {expanded === r.vessel_name && r.jobs.map((j: any) => (
                            <tr key={j.id} className="bg-gray-50 text-xs text-gray-600">
                              <td className="px-3 py-1 pl-7">{formatDateEU(j.cleaned_date)}{j.booking_number ? ` · ${j.booking_number}` : ''}</td>
                              <td></td>
                              <td className="text-right px-2 py-1">{(parseFloat(j.amount) || 0).toFixed(2)} €</td>
                              <td colSpan={2} className="text-right px-3 py-1">
                                <span className={`px-2 py-0.5 rounded font-bold ${j.paid ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                  {j.paid ? '✅ Πληρώθηκε' : '⏳ Εκκρεμεί'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-blue-200 font-bold" style={{ background: '#e0f7ff' }}>
                        <td className="px-3 py-2 text-blue-900">ΣΥΝΟΛΟ</td>
                        <td className="text-center px-2 py-2">{paySummary.totals.count}</td>
                        <td className="text-right px-2 py-2">{paySummary.totals.charged.toFixed(2)} €</td>
                        <td className="text-right px-2 py-2 text-green-700">{paySummary.totals.paid.toFixed(2)} €</td>
                        <td className={`text-right px-3 py-2 ${paySummary.totals.balance === 0 ? 'text-green-600' : 'text-red-600'}`}>{paySummary.totals.balance.toFixed(2)} €</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => exportPaymentsPDF(paySummary, periodTxt)}
                    className="flex-1 py-3 rounded-xl font-bold text-white shadow" style={{ background: GATE_BLUE }}>📄 Export PDF</button>
                  <button onClick={() => exportPaymentsWord(paySummary, periodTxt)}
                    className="flex-1 py-3 rounded-xl font-bold text-white shadow bg-teal-600 hover:bg-teal-700">📝 Export Word</button>
                </div>
              </>
            )}
          </>
        )}

        {/* TAB 4: Στοιχεία Εταιρείας */}
        {tab === 'company' && (
          <div className="bg-white rounded-lg p-4 shadow space-y-3">
            <div className="font-bold text-lg" style={{ color: '#1e90ff' }}>🏢 Στοιχεία Εταιρείας</div>
            {[
              ['company_name', 'Επωνυμία'], ['address', 'Διεύθυνση'], ['afm', 'ΑΦΜ'],
              ['doy', 'ΔΟΥ'], ['email', 'Email'], ['phone', 'Τηλέφωνο'], ['iban', 'Αριθμός Λογαριασμού (IBAN)']
            ].map(([key, label]) => (
              <label key={key} className="block text-sm text-gray-600">{label}
                <input type="text" value={company[key] || ''}
                  onChange={e => setCompany({ ...company, [key]: e.target.value })}
                  className="w-full mt-0.5 px-3 py-2 border border-gray-300 rounded text-base" />
              </label>
            ))}
            <button onClick={saveCompany}
              style={{ background: GATE_BLUE }}
              className="w-full py-3 rounded-xl font-bold text-white shadow">
              💾 Αποθήκευση
            </button>
            {companySaved && <div className="text-center text-green-600 font-semibold text-sm">✅ Αποθηκεύτηκε</div>}
          </div>
        )}
      </div>
    </div>
  );
};

export default CleaningDashboard;
